/**
 * 図の器の表示幅を測るフック。
 *
 * US 単独の図と左右比較の両方が使う。片方だけが持つと、もう片方が同じ観測を書き直すことになり、
 * 「何を測るか」（中身込みの `clientWidth` ではなく外枠）の判断が 2 箇所へ分かれる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 図の器の表示幅を測り、大きさが変わるたびに測り直す。
 * @graph-connects none
 */
import { useCallback, useRef, useState } from 'react';

/**
 * 器の表示幅を測り続ける。
 *
 * 測るのは**外枠**（`getBoundingClientRect`）であって、中身の入った `clientWidth` ではない。
 * 中身込みの幅から倍率を決めると、縮めた結果スクロールバーが消えて幅が広がり、また倍率が
 * 上がってスクロールバーが戻る、という往復が起こりうる。外枠は列の割り付けだけで決まるので
 * 中身に影響されない。
 *
 * ref を受け取った時点で観測を始める。描画前は器が無いので 0 を返し、倍率は 1（原寸）に倒れる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 図の器の表示幅を測り、大きさが変わるたびに測り直す。
 * @graph-connects none
 */
export function useMeasuredWidth(): {
  /** 測れた幅。まだ測っていなければ 0。 */
  width: number;
  /**
   * 測り終えたか。
   *
   * 幅が 0 かどうかとは別に持つ。レイアウトを持たない環境（テストの jsdom）では実測しても
   * 0 が返るので、「0 だからまだ測っていない」と読むと器の中身が永久に描かれない。
   */
  measured: boolean;
  /** 測る対象の要素を渡す ref コールバック。 */
  attach: (element: HTMLElement | null) => void;
} {
  const [state, setState] = useState({ width: 0, measured: false });
  const observer = useRef<ResizeObserver | null>(null);

  const attach = useCallback((element: HTMLElement | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (element === null) return;

    const measure = () => {
      setState({ width: element.getBoundingClientRect().width, measured: true });
    };
    measure();
    const next = new ResizeObserver(measure);
    next.observe(element);
    observer.current = next;
  }, []);

  return { ...state, attach };
}
